/**
 * Metadata Context
 *
 * Provides global access to metadata loading, caching, and validation throughout the application.
 * Manages templates, fields, datasets, and enrichment configurations.
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import {
  loadMetadataFile,
  loadAllMetadata,
  preloadMetadata,
  getCacheStats,
  clearCache,
  getLoadingStates,
  getErrorStates,
} from "../services/metadataLoader.js";
import {
  validateMetadata,
  parseMetadata,
  ValidationResult,
} from "../services/metadataValidator.js";

/**
 * Metadata context state structure
 */
const initialState = {
  // Core metadata
  templates: {},
  fields: {},
  datasets: {},
  enrichments: {},

  // Loading states
  loading: {},

  // Error states
  errors: {},

  // Validation results
  validation: {},

  // Cache information
  cache: {
    enabled: true,
    stats: {
      total: 0,
      active: 0,
      expired: 0,
      capacity: 100,
    },
  },

  // System state
  initialized: false,
  lastUpdated: null,
  preloadComplete: false,
};

/**
 * Action types for metadata reducer
 */
const ACTIONS = {
  // Loading actions
  SET_LOADING: "SET_LOADING",
  CLEAR_LOADING: "CLEAR_LOADING",

  // Data actions
  SET_METADATA: "SET_METADATA",
  SET_TEMPLATE: "SET_TEMPLATE",
  SET_FIELD: "SET_FIELD",
  SET_DATASET: "SET_DATASET",
  SET_ENRICHMENT: "SET_ENRICHMENT",

  // Error actions
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",

  // Validation actions
  SET_VALIDATION: "SET_VALIDATION",
  CLEAR_VALIDATION: "CLEAR_VALIDATION",

  // Cache actions
  UPDATE_CACHE_STATS: "UPDATE_CACHE_STATS",

  // System actions
  SET_INITIALIZED: "SET_INITIALIZED",
  SET_PRELOAD_COMPLETE: "SET_PRELOAD_COMPLETE",
  RESET_STATE: "RESET_STATE",
};

/**
 * Metadata reducer function
 */
function metadataReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.key]: action.isLoading,
        },
      };

    case ACTIONS.CLEAR_LOADING:
      const newLoading = { ...state.loading };
      if (action.key) {
        delete newLoading[action.key];
      } else {
        Object.keys(newLoading).forEach((key) => delete newLoading[key]);
      }
      return {
        ...state,
        loading: newLoading,
      };

    case ACTIONS.SET_METADATA:
      return {
        ...state,
        [action.category]: {
          ...state[action.category],
          ...action.data,
        },
        lastUpdated: new Date().toISOString(),
      };

    case ACTIONS.SET_TEMPLATE:
      return {
        ...state,
        templates: {
          ...state.templates,
          [action.id]: action.data,
        },
        lastUpdated: new Date().toISOString(),
      };

    case ACTIONS.SET_FIELD:
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.id]: action.data,
        },
        lastUpdated: new Date().toISOString(),
      };

    case ACTIONS.SET_DATASET:
      return {
        ...state,
        datasets: {
          ...state.datasets,
          [action.id]: action.data,
        },
        lastUpdated: new Date().toISOString(),
      };

    case ACTIONS.SET_ENRICHMENT:
      return {
        ...state,
        enrichments: {
          ...state.enrichments,
          [action.id]: action.data,
        },
        lastUpdated: new Date().toISOString(),
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.key]: action.error,
        },
      };

    case ACTIONS.CLEAR_ERROR:
      const newErrors = { ...state.errors };
      if (action.key) {
        delete newErrors[action.key];
      } else {
        Object.keys(newErrors).forEach((key) => delete newErrors[key]);
      }
      return {
        ...state,
        errors: newErrors,
      };

    case ACTIONS.SET_VALIDATION:
      return {
        ...state,
        validation: {
          ...state.validation,
          [action.key]: action.result,
        },
      };

    case ACTIONS.CLEAR_VALIDATION:
      const newValidation = { ...state.validation };
      if (action.key) {
        delete newValidation[action.key];
      } else {
        Object.keys(newValidation).forEach((key) => delete newValidation[key]);
      }
      return {
        ...state,
        validation: newValidation,
      };

    case ACTIONS.UPDATE_CACHE_STATS:
      return {
        ...state,
        cache: {
          ...state.cache,
          stats: action.stats,
        },
      };

    case ACTIONS.SET_INITIALIZED:
      return {
        ...state,
        initialized: action.initialized,
      };

    case ACTIONS.SET_PRELOAD_COMPLETE:
      return {
        ...state,
        preloadComplete: action.complete,
      };

    case ACTIONS.RESET_STATE:
      return {
        ...initialState,
        cache: {
          ...initialState.cache,
          enabled: state.cache.enabled,
        },
      };

    default:
      return state;
  }
}

/**
 * Create metadata context
 */
const MetadataContext = createContext();

/**
 * Metadata Provider Component
 */
export function MetadataProvider({
  children,
  enablePreload = true,
  enableAutoRefresh = false,
}) {
  const [state, dispatch] = useReducer(metadataReducer, initialState);

  /**
   * Update cache statistics
   */
  const updateCacheStats = useCallback(() => {
    const stats = getCacheStats();
    dispatch({ type: ACTIONS.UPDATE_CACHE_STATS, stats });
  }, []);

  /**
   * Set loading state for a specific key
   */
  const setLoading = useCallback((key, isLoading) => {
    dispatch({ type: ACTIONS.SET_LOADING, key, isLoading });
  }, []);

  /**
   * Set error for a specific key
   */
  const setError = useCallback((key, error) => {
    dispatch({ type: ACTIONS.SET_ERROR, key, error });
  }, []);

  /**
   * Clear error for a specific key
   */
  const clearError = useCallback((key) => {
    dispatch({ type: ACTIONS.CLEAR_ERROR, key });
  }, []);

  /**
   * Load and validate metadata file
   */
  const loadMetadata = useCallback(
    async (path, options = {}) => {
      const loadKey = `load_${path}`;

      try {
        setLoading(loadKey, true);
        clearError(loadKey);

        // Load the metadata file
        const rawData = await loadMetadataFile(path, options);

        // Validate the metadata
        const validationResult = validateMetadata(rawData, options.type);

        // Store validation result
        dispatch({
          type: ACTIONS.SET_VALIDATION,
          key: path,
          result: validationResult,
        });

        if (!validationResult.valid && options.strict !== false) {
          throw new Error(
            `Validation failed for ${path}: ${validationResult.getFormattedErrors().join(", ")}`
          );
        }

        // Determine category and ID
        const category = options.category || path.split("/")[1] || "misc";
        const id =
          options.id ||
          rawData.id ||
          path.split("/").pop().replace(".json", "");

        // Store the metadata
        switch (category) {
          case "templates":
            dispatch({ type: ACTIONS.SET_TEMPLATE, id, data: rawData });
            break;
          case "fields":
            dispatch({ type: ACTIONS.SET_FIELD, id, data: rawData });
            break;
          case "datasets":
            dispatch({ type: ACTIONS.SET_DATASET, id, data: rawData });
            break;
          case "enrichments":
            dispatch({ type: ACTIONS.SET_ENRICHMENT, id, data: rawData });
            break;
          default:
            dispatch({
              type: ACTIONS.SET_METADATA,
              category,
              data: { [id]: rawData },
            });
        }

        return rawData;
      } catch (error) {
        setError(loadKey, error);
        throw error;
      } finally {
        setLoading(loadKey, false);
        updateCacheStats();
      }
    },
    [setLoading, clearError, setError, updateCacheStats]
  );

  /**
   * Load template by ID
   */
  const loadTemplate = useCallback(
    async (templateId, options = {}) => {
      const path = `/metadata/templates/${templateId}.json`;
      return loadMetadata(path, {
        ...options,
        type: "template",
        category: "templates",
        id: templateId,
      });
    },
    [loadMetadata]
  );

  /**
   * Load fields by filename
   */
  const loadFields = useCallback(
    async (fieldsFile, options = {}) => {
      const path = `/metadata/fields/${fieldsFile}.json`;
      return loadMetadata(path, {
        ...options,
        type: "field",
        category: "fields",
        id: fieldsFile,
      });
    },
    [loadMetadata]
  );

  /**
   * Load enrichment by ID
   */
  const loadEnrichment = useCallback(
    async (enrichmentId, options = {}) => {
      const path = `/metadata/enrichments/${enrichmentId}.json`;
      return loadMetadata(path, {
        ...options,
        type: "enrichment",
        category: "enrichments",
        id: enrichmentId,
      });
    },
    [loadMetadata]
  );

  /**
   * Load all core metadata
   */
  const loadAllCoreMetadata = useCallback(
    async (options = {}) => {
      const loadKey = "load_all";

      try {
        setLoading(loadKey, true);
        clearError(loadKey);

        const allMetadata = await loadAllMetadata(options);

        // Process and store each category
        Object.entries(allMetadata).forEach(([category, data]) => {
          dispatch({ type: ACTIONS.SET_METADATA, category, data });
        });

        dispatch({ type: ACTIONS.SET_INITIALIZED, initialized: true });

        return allMetadata;
      } catch (error) {
        setError(loadKey, error);
        throw error;
      } finally {
        setLoading(loadKey, false);
        updateCacheStats();
      }
    },
    [setLoading, clearError, setError, updateCacheStats]
  );

  /**
   * Preload metadata for better performance
   */
  const preloadAllMetadata = useCallback(
    async (options = {}) => {
      const loadKey = "preload";

      try {
        setLoading(loadKey, true);

        await preloadMetadata(options);
        dispatch({ type: ACTIONS.SET_PRELOAD_COMPLETE, complete: true });
      } catch (error) {
        console.warn("Preload failed:", error);
        // Don't throw - preload failures should be non-blocking
      } finally {
        setLoading(loadKey, false);
        updateCacheStats();
      }
    },
    [setLoading, updateCacheStats]
  );

  /**
   * Clear all cached metadata
   */
  const clearAllCache = useCallback(
    (pattern) => {
      clearCache(pattern);
      dispatch({ type: ACTIONS.RESET_STATE });
      updateCacheStats();
    },
    [updateCacheStats]
  );

  /**
   * Get metadata by category and ID
   */
  const getMetadata = useCallback(
    (category, id) => {
      return state[category]?.[id];
    },
    [state]
  );

  /**
   * Get template by ID
   */
  const getTemplate = useCallback(
    (templateId) => {
      return state.templates[templateId];
    },
    [state.templates]
  );

  /**
   * Get fields by filename
   */
  const getFields = useCallback(
    (fieldsFile) => {
      return state.fields[fieldsFile];
    },
    [state.fields]
  );

  /**
   * Get enrichment by ID
   */
  const getEnrichment = useCallback(
    (enrichmentId) => {
      return state.enrichments[enrichmentId];
    },
    [state.enrichments]
  );

  /**
   * Check if metadata is loading
   */
  const isLoading = useCallback(
    (key) => {
      if (key) {
        return !!state.loading[key] || !!state.loading[`load_${key}`];
      }
      return Object.values(state.loading).some(Boolean);
    },
    [state.loading]
  );

  /**
   * Get error for a specific key
   */
  const getError = useCallback(
    (key) => {
      return state.errors[key] || state.errors[`load_${key}`];
    },
    [state.errors]
  );

  /**
   * Get validation result for a specific key
   */
  const getValidation = useCallback(
    (key) => {
      return state.validation[key];
    },
    [state.validation]
  );

  /**
   * Check if specific metadata exists
   */
  const hasMetadata = useCallback(
    (category, id) => {
      return !!(state[category] && state[category][id]);
    },
    [state]
  );

  /**
   * Auto-initialize on mount
   */
  useEffect(() => {
    if (!state.initialized) {
      loadAllCoreMetadata().catch(console.error);
    }
  }, [state.initialized, loadAllCoreMetadata]);

  /**
   * Auto-preload if enabled
   */
  useEffect(() => {
    if (enablePreload && state.initialized && !state.preloadComplete) {
      preloadAllMetadata().catch(console.error);
    }
  }, [
    enablePreload,
    state.initialized,
    state.preloadComplete,
    preloadAllMetadata,
  ]);

  /**
   * Auto-refresh cache stats periodically
   */
  useEffect(() => {
    if (enableAutoRefresh) {
      const interval = setInterval(updateCacheStats, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [enableAutoRefresh, updateCacheStats]);

  /**
   * Context value
   */
  const contextValue = {
    // State
    ...state,

    // Loading functions
    loadMetadata,
    loadTemplate,
    loadFields,
    loadEnrichment,
    loadAllCoreMetadata,
    preloadAllMetadata,

    // Getter functions
    getMetadata,
    getTemplate,
    getFields,
    getEnrichment,

    // Utility functions
    isLoading,
    getError,
    getValidation,
    hasMetadata,
    clearError,
    clearAllCache,
    updateCacheStats,
  };

  return (
    <MetadataContext.Provider value={contextValue}>
      {children}
    </MetadataContext.Provider>
  );
}

/**
 * Hook to use metadata context
 */
export function useMetadata() {
  const context = useContext(MetadataContext);
  if (!context) {
    throw new Error("useMetadata must be used within a MetadataProvider");
  }
  return context;
}

/**
 * Hook to use specific template
 */
export function useTemplate(templateId) {
  const { getTemplate, loadTemplate, isLoading, getError } = useMetadata();

  const template = getTemplate(templateId);
  const loading = isLoading(`templates/${templateId}`);
  const error = getError(`templates/${templateId}`);

  // Auto-load if not present
  useEffect(() => {
    if (!template && !loading && !error) {
      loadTemplate(templateId).catch(console.error);
    }
  }, [template, loading, error, templateId, loadTemplate]);

  return { template, loading, error };
}

/**
 * Hook to use specific fields
 */
export function useFields(fieldsFile) {
  const { getFields, loadFields, isLoading, getError } = useMetadata();

  const fields = getFields(fieldsFile);
  const loading = isLoading(`fields/${fieldsFile}`);
  const error = getError(`fields/${fieldsFile}`);

  // Auto-load if not present
  useEffect(() => {
    if (!fields && !loading && !error) {
      loadFields(fieldsFile).catch(console.error);
    }
  }, [fields, loading, error, fieldsFile, loadFields]);

  return { fields, loading, error };
}

/**
 * Hook to use specific enrichment
 */
export function useEnrichment(enrichmentId) {
  const { getEnrichment, loadEnrichment, isLoading, getError } = useMetadata();

  const enrichment = getEnrichment(enrichmentId);
  const loading = isLoading(`enrichments/${enrichmentId}`);
  const error = getError(`enrichments/${enrichmentId}`);

  // Auto-load if not present
  useEffect(() => {
    if (!enrichment && !loading && !error) {
      loadEnrichment(enrichmentId).catch(console.error);
    }
  }, [enrichment, loading, error, enrichmentId, loadEnrichment]);

  return { enrichment, loading, error };
}

export default MetadataContext;
